import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { ProductCard } from "../components/ProductCard";
import { ArrowRight, Truck, Boxes, ShieldCheck, Building2, Briefcase, HardHat, FileText } from "lucide-react";

const CAT_IMAGES = {
  plumbing: "https://images.pexels.com/photos/7937299/pexels-photo-7937299.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  sanitary: "https://images.pexels.com/photos/7587747/pexels-photo-7587747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hardware: "https://images.unsplash.com/photo-1507647185148-e27abccb6be5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  building: "https://images.unsplash.com/photo-1770823556202-2eba715a415b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const HERO_BG = "https://images.pexels.com/photos/10284048/pexels-photo-10284048.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Home() {
  const { t, lang } = useI18n();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 8)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0F172A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="label-mono text-[#FFB800] mb-6">{t("home.kicker")}</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tighter">
              {t("home.heroTitle")}
            </h1>
            <p className="mt-6 max-w-xl text-slate-300 text-base sm:text-lg leading-relaxed">
              {t("home.heroSub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn">
                <button className="btn-maroon px-8 py-4 font-bold tracking-wide flex items-center gap-2">
                  {t("home.shopNow")} <ArrowRight className="w-4 h-4 rtl-flip" />
                </button>
              </Link>
              <Link to="/projects-enquiry" data-testid="hero-inquiry-btn">
                <button className="px-8 py-4 font-bold tracking-wide border border-white/40 hover:bg-white hover:text-slate-900 transition">
                  {t("home.ctaBtn")}
                </button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 hidden md:grid grid-cols-2 gap-3 fade-up">
            <Stat number="2,400+" label={t("home.stats.products")} />
            <Stat number="26" label={t("home.stats.years")} />
            <Stat number="24h" label={t("home.stats.delivery")} />
            <Stat number="80+" label={t("home.stats.brands")} />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="mb-10">
          <div className="label-mono text-slate-500">{lang === "ar" ? "نقدم" : "What we offer"}</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-2">{t("home.servicesTitle")}</h2>
        </div>
        <div className="grid md:grid-cols-12 gap-0 border border-slate-200">
          <ServiceCard icon={<Boxes className="w-6 h-6" />} title={t("home.services.trading.t")} body={t("home.services.trading.d")} to="/shop" span="md:col-span-4" />
          <ServiceCard icon={<Building2 className="w-6 h-6" />} title={t("home.services.contracting.t")} body={t("home.services.contracting.d")} to="/projects-enquiry" span="md:col-span-4" mid />
          <ServiceCard icon={<FileText className="w-6 h-6" />} title={t("home.services.projects.t")} body={t("home.services.projects.d")} to="/projects-enquiry" span="md:col-span-4" />
          <ServiceCard icon={<Briefcase className="w-6 h-6" />} title={t("home.services.jobs.t")} body={t("home.services.jobs.d")} to="/jobs" span="md:col-span-6" top />
          <ServiceCard icon={<HardHat className="w-6 h-6" />} title={t("home.services.talent.t")} body={t("home.services.talent.d")} to="/talent-pool" span="md:col-span-6" top mid />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-12 mb-2">
            <div className="label-mono text-slate-500">{t("home.browseCategories")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-2">{t("nav.categories")}</h2>
          </div>
          {[
            { id: "plumbing", title: t("categories.plumbing"), span: "md:col-span-6" },
            { id: "sanitary", title: t("categories.sanitary"), span: "md:col-span-6" },
            { id: "hardware", title: t("categories.hardware"), span: "md:col-span-4" },
            { id: "building", title: t("categories.building"), span: "md:col-span-8" },
          ].map((c) => (
            <Link
              key={c.id}
              to={`/shop?category=${c.id}`}
              className={`${c.span} relative group overflow-hidden border border-slate-200 aspect-[16/9] md:aspect-auto md:min-h-[260px]`}
              data-testid={`category-link-${c.id}`}
            >
              <img src={CAT_IMAGES[c.id]} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 text-white flex items-end justify-between">
                <div>
                  <div className="label-mono text-[#FFB800]">{lang === "ar" ? "تسوق" : "Shop"}</div>
                  <div className="font-display font-black text-2xl sm:text-3xl mt-1">{c.title}</div>
                </div>
                <div className="w-10 h-10 bg-white text-slate-900 flex items-center justify-center group-hover:bg-[#FFB800]">
                  <ArrowRight className="w-5 h-5 rtl-flip" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-slate-50 py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <div className="label-mono text-slate-500">{t("home.featured")}</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
                {lang === "ar" ? "أحدث الإمدادات" : "Latest stock arrivals"}
              </h2>
            </div>
            <Link to="/shop" className="text-sm font-bold underline-offset-4 hover:underline flex items-center gap-1">
              {t("home.shopNow")} <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="label-mono text-slate-500">{lang === "ar" ? "لماذا نحن" : "Why us"}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">{t("home.whyTitle")}</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-0 border border-slate-200">
            <Feature icon={<Boxes className="w-6 h-6" />} title={t("home.why1Title")} body={t("home.why1")} />
            <Feature icon={<ShieldCheck className="w-6 h-6" />} title={t("home.why2Title")} body={t("home.why2")} mid />
            <Feature icon={<Truck className="w-6 h-6" />} title={t("home.why3Title")} body={t("home.why3")} />
          </div>
        </div>
      </section>

      {/* CONTACT & SOCIAL */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label-mono text-slate-500">{t("contact.title")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">{t("contact.title")}</h2>
            <p className="mt-4 text-slate-600 max-w-md">{t("contact.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://wa.me/97433732203" target="_blank" rel="noreferrer" className="btn-maroon px-6 py-3 font-bold tracking-wide flex items-center gap-2" data-testid="contact-wa-btn">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.555-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.46.738-1.034 3.788 3.553-.985z"/></svg>
                {t("contact.whatsapp")}
              </a>
              <a href="tel:+97433732203" className="px-6 py-3 font-bold tracking-wide border-2 border-slate-900 hover:bg-slate-900 hover:text-white transition" data-testid="contact-call-btn">
                +974 3373 2203
              </a>
            </div>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-0 border border-slate-200 bg-white">
            <ContactItem label={t("contact.website")} value="www.alomarah.com" href="https://www.alomarah.com" />
            <ContactItem label={t("contact.email")} value="info@alomarah.com" href="mailto:info@alomarah.com" mid />
            <ContactItem label={t("contact.phone")} value="+974 3373 2203" href="tel:+97433732203" top />
            <ContactItem label={t("contact.address")} value={t("footer.address")} href="https://maps.google.com/?q=Doha,Qatar" mid top />
            <div className="sm:col-span-2 p-6 border-t border-slate-200">
              <div className="label-mono text-slate-500 mb-4">{t("contact.followUs")}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <SocialBtn href="https://facebook.com/alomarah" testid="home-fb" label="Facebook">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.92 3.78-3.92 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.79 8.43-4.94 8.43-9.94z"/></svg>
                </SocialBtn>
                <SocialBtn href="https://instagram.com/alomarah" testid="home-ig" label="Instagram">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.16c3.2 0 3.58 0 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.34 4.14.63a5.86 5.86 0 0 0-2.13 1.38A5.86 5.86 0 0 0 .63 4.14C.34 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.27 2.15.56 2.91a5.86 5.86 0 0 0 1.38 2.13 5.86 5.86 0 0 0 2.13 1.38c.76.29 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.27 2.91-.56a5.86 5.86 0 0 0 2.13-1.38 5.86 5.86 0 0 0 1.38-2.13c.29-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.27-2.15-.56-2.91a5.86 5.86 0 0 0-1.38-2.13A5.86 5.86 0 0 0 19.86.63C19.1.34 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg>
                </SocialBtn>
                <SocialBtn href="https://wa.me/97433732203" testid="home-wa" label="WhatsApp">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.555-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.46.738-1.034 3.788 3.553-.985z"/></svg>
                </SocialBtn>
                <SocialBtn href="https://www.tiktok.com/@alomarah" testid="home-tt" label="TikTok">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.92a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z"/></svg>
                </SocialBtn>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#8D1B3D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="label-mono text-[#FFB800]">B2B</div>
            <h3 className="font-display font-black text-3xl sm:text-4xl mt-2">{t("home.ctaTitle")}</h3>
            <p className="mt-3 text-white/80 max-w-xl">{t("home.ctaSub")}</p>
          </div>
          <Link to="/projects-enquiry" className="bg-white text-[#8D1B3D] px-8 py-4 font-bold tracking-wide hover:bg-[#FFB800] hover:text-slate-900 transition" data-testid="cta-inquiry">
            {t("home.ctaBtn")}
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div className="border border-white/15 p-6 backdrop-blur-sm bg-white/5">
      <div className="font-display font-black text-3xl text-white">{number}</div>
      <div className="label-mono text-slate-300 mt-1">{label}</div>
    </div>
  );
}

function Feature({ icon, title, body, mid }) {
  return (
    <div className={`p-8 ${mid ? "sm:border-x border-slate-200" : ""}`}>
      <div className="w-10 h-10 bg-[#8D1B3D] text-white flex items-center justify-center mb-5">{icon}</div>
      <div className="font-display font-bold text-lg">{title}</div>
      <div className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</div>
    </div>
  );
}

function ServiceCard({ icon, title, body, to, span, mid, top }) {
  return (
    <Link
      to={to}
      className={`${span} p-7 group hover:bg-slate-50 transition-colors ${mid ? "md:border-x border-slate-200" : ""} ${!top ? "border-b md:border-b-0 md:border-t-0 border-slate-200" : "border-b border-slate-200"}`}
    >
      <div className="w-12 h-12 bg-[#8D1B3D] text-white flex items-center justify-center mb-5 group-hover:bg-[#711531]">{icon}</div>
      <div className="font-display font-bold text-xl">{title}</div>
      <div className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</div>
      <div className="mt-4 inline-flex items-center gap-1 text-xs label-mono text-[#8D1B3D]">
        Open <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
      </div>
    </Link>
  );
}

function ContactItem({ label, value, href, mid, top }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className={`p-6 hover:bg-slate-50 transition-colors ${mid ? "sm:border-s border-slate-200" : ""} ${top ? "border-t border-slate-200" : ""}`}
    >
      <div className="label-mono text-slate-500">{label}</div>
      <div className="font-display font-bold text-lg mt-1.5 break-all">{value}</div>
    </a>
  );
}

function SocialBtn({ href, children, testid, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      data-testid={testid}
      className="w-11 h-11 bg-slate-900 text-white hover:bg-[#8D1B3D] flex items-center justify-center transition-colors"
    >
      {children}
    </a>
  );
}
